<template>
  <div style="display: none;"></div>
</template>

<script>
import { createToast } from 'mosha-vue-toastify'
import { defineComponent, onMounted, ref } from 'vue'
import consumer from '../../../javascript/channels/consumer'

// Global flag to prevent multiple subscriptions
let globalSubscription = null;

export default defineComponent({
  name: 'GlobalNotifications',
  setup() {
    const currentUserId = ref(null);
    const offlineNotifications = ref([]);
    const isSubscribed = ref(false);
    const subscription = ref(null);

    // User ID detection
    const getCurrentUserId = () => {
      // Try to get user ID from various sources
      if (window.currentUser && window.currentUser.id) {
        currentUserId.value = window.currentUser.id;
      } else if (document.querySelector('meta[name="current-user-id"]')) {
        currentUserId.value = document.querySelector('meta[name="current-user-id"]').getAttribute('content');
      } else if (window.gon && window.gon.current_user_id) {
        currentUserId.value = window.gon.current_user_id;
      } else {
        const userElement = document.querySelector('[data-user-id]');
        if (userElement) {
          currentUserId.value = userElement.getAttribute('data-user-id');
        }
      }
    };

    // Subscribe to ActionCable notifications
    const subscribeToNotifications = () => {
      if (!currentUserId.value) {
        return;
      }

      // Check if there's already a global subscription
      if (globalSubscription) {
        subscription.value = globalSubscription;
        isSubscribed.value = true;
        return;
      }

      try {
        subscription.value = consumer.subscriptions.create(
          { channel: "NotificationsChannel", user_id: currentUserId.value },
          {
            connected() {
              isSubscribed.value = true;
              globalSubscription = subscription.value;
            },

            disconnected() {
              isSubscribed.value = false;
              globalSubscription = null;
            },

            rejected() {
              console.error('GlobalNotifications: Failed to connect to notifications channel');
              isSubscribed.value = false;
              globalSubscription = null;
            },

            received(data) {
              // Display notification as toast
              createToast(
                {
                  title: data.title,
                  description: data.message,
                  type: 'success'
                },
                {
                  timeout: 6000,
                  position: 'bottom-center',
                  dangerouslyUseHTMLString: true
                }
              );

              // Mark notification as read
              if (data.notification_id) {
                markNotificationAsRead(data.notification_id);
              }
            }
          }
        );
      } catch (error) {
        console.error('GlobalNotifications: Error subscribing to notifications:', error);
      }
    };

    // Mark notification as read
    const markNotificationAsRead = async (notificationId) => {
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        await fetch(`/api/v1/events/mark_notification_read`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ notification_id: notificationId })
        });
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    };

    // Fetch offline notifications
    const fetchOfflineNotifications = async () => {
      if (!currentUserId.value) {
        return;
      }

      try {
        // Get CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        const response = await fetch('/api/v1/events/notifications', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const notifications = await response.json();
          offlineNotifications.value = notifications || [];

          // Display notifications as toasts
          offlineNotifications.value.forEach(notification => {
            createToast(
              {
                title: notification.title,
                description: notification.message,
                type: 'success'
              },
              {
                timeout: 6000,
                position: 'bottom-center',
                dangerouslyUseHTMLString: true
              }
            );

            // Mark notification as read
            if (notification.id) {
              markNotificationAsRead(notification.id);
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch offline notifications:', error);
      }
    };

    // Unsubscribe from notifications
    const unsubscribeFromNotifications = () => {
      if (subscription.value) {
        subscription.value.unsubscribe();
        subscription.value = null;
        isSubscribed.value = false;
        globalSubscription = null;
      }
    };

    // Component setup
    onMounted(() => {
      getCurrentUserId();
      // Subscribe to real-time notifications
      subscribeToNotifications();
      // Fetch offline notifications
      fetchOfflineNotifications();
    });

    // Cleanup on unmount
    const cleanup = () => {
      unsubscribeFromNotifications();
    };

    return {
      currentUserId,
      offlineNotifications,
      isSubscribed,
      cleanup
    };
  }
});
</script>
